import type { ClassDefinition } from '../../schemas/classDefinition';
import { bardoAdvanced2to5Moves } from '../moves/bardo/advanced-2-5';
import { bardoAdvanced6to10Moves } from '../moves/bardo/advanced-6-10';
import { bardoStartingAutoMoves, bardoStartingChoiceMoves } from '../moves/bardo/initial';

/**
 * Definizione della classe **Bardo** (manuale V1, rr. 2235-2601).
 *
 * Terza classe trascritta. Pattern principali stress-testati:
 * - **Prima vera applicazione di `pickCount: 2`** su `StartingMoveChoiceGroup`
 *   (manuale r. 2360: "scegli **due** delle seguenti mosse")
 * - **Prima vera applicazione di 6/6** sul refine FI-3 (`maxBonds: 6` con
 *   6 `bondTemplates`)
 * - **Catena prerequisiti a 3 step**: *Versatile* → *Eclettico* → *Poliedrico*
 * - **Archetipo con sotto-scelta a 12 opzioni** (Mezz'orco, riuso pattern
 *   *Avvelenatore* del Ladro)
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base 6, Carico base 9
 * - 5 archetipi razza-tematici
 * - 3 mosse iniziali automatiche + 4 mosse iniziali di scelta (`pickCount: 2`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (42 mosse totali)
 * - 6 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - **Non casteggiante**: `spellcasting` resta `undefined`
 * - **6 monete** + **3 monete** in opzioni di equipaggiamento, modellate
 *   come Item con `quantity: 6` e `quantity: 3` (FI-16 risolto)
 */
export const bardoClassDefinition: ClassDefinition = {
  id: 'bardo',
  name: 'Bardo',

  intro:
    "Ascolta. Le leggende narrano di eroi forgiati in battaglia, di tesori che abbagliano, di regni che sorgono e cadono. Ma tu, Bardo, conosci la verità più antica: il vero potere pulsa nel cuore vibrante della *Parola*. Non nelle armi o negli incantesimi effimeri, ma nella forza arcana e inesauribile del *Linguaggio*. Tu hai ascoltato il sussurro delle foreste ancestrali, decrittato gli antichi tomi dimenticati, intercettato i segreti bisbigliati nelle corti e nelle taverne, decifrato il caos eloquente delle strade. Sai che ogni voce, ogni melodia, ogni racconto è eco di un passato vivo, frammento di una memoria collettiva che attende solo di essere risvegliata.\n\nTi ergi come Custode di questa Memoria Verbale, come Maestro dell'Arte della *Parola*, come fonte di ispirazione, di emozione e di catarsi. La tua magia non discende dai Cieli, non sgorga dalla Terra, ma emana dal tuo dominio totale sul *Linguaggio*, dalla tua connessione intima con esso, dalla tua comprensione di ogni sua sfumatura semantica e ritmica. La tua parola è incantesimo, arma e cura. Plasma la realtà, influenza le menti, accende i cuori, forgia le leggende. Non sei un semplice menestrello che ripete storie altrui, ma un Demiurgo Verbale che dona vita nuova ai miti antichi, che reinterpreta il passato per illuminare il presente, che forgia leggende inedite per il futuro.\n\nSfodera la tua lingua fluente e tagliente, Bardo. Affina la tua capacità di osservare e comprendere l'animo umano. Sii versatile e adattabile come il linguaggio stesso. E ascolta il richiamo urgente dell'Avventura, perché è nel mondo, nelle esperienze vissute, nelle storie raccolte, che placherai la tua sete inesauribile di conoscenza e narrazione, che troverai la materia prima per la tua magia unica e potente: la *Parola*!",

  // Manuale V1, rr. 2246-2252
  suggestedNames: [
    // Umano
    'Lyra',
    'Baldric',
    'Dunwick',
    'Willem',
    'Florian',
    'Seraphine',
    'Lily',
    'Flora',
    'Cassandra',
    // Elfo
    'Astrafel',
    'Daelwyn',
    'Feliana',
    'Damarra',
    'Sistranalle',
    'Pendrell',
    'Melliandre',
    'Dagoliir',
    // Nano
    'Rombrandro',
    'Thecras',
    'Dabel',
    'Adha',
    'Baelme',
    'Darglo',
    'Bima',
    'Muris',
    'Darda',
    // Halfling
    'Dodrida',
    'Rufast',
    'Madmira',
    'Berto',
    'Rosa',
    'Rabard',
    'Estel',
    'Dinero',
    'Perman',
    // Mezz'orco
    'Luga',
    'Gullu',
    'Muz',
    'Harro',
    'Gola',
    'Gluka',
    'Baagju',
    'Dog-ju',
    'Gridana',
    'Nag',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 2254-2260
  aspetti: [
    {
      label: 'Occhi',
      options: ['Sapienti', 'Ardenti', 'Gioiosi'],
    },
    {
      label: 'Capelli',
      options: ['Acconciatura elegante', 'Capelli selvaggi', 'Cappello alla moda'],
    },
    {
      label: 'Abiti',
      options: ['Eleganti', 'Da viaggio', 'Poveri'],
    },
    {
      label: 'Corpo',
      options: ['Allenato', 'Ben nutrito', 'Esile'],
    },
  ],

  // Manuale V1, rr. 2262-2268
  damageDie: 'd6',
  baseHp: 6,
  baseLoad: 9,

  // Manuale V1, rr. 2270-2278
  alignmentPrecepts: {
    buono: 'Usi la tua arte per aiutare qualcuno.',
    legale: 'Usi la tua arte per mantenere lo status quo.',
    neutrale: 'Eviti il conflitto o calmi una situazione tesa.',
    caotico: 'Spingi qualcuno ad agire impulsivamente e in modo decisivo.',
    malvagio: 'Fai il doppio gioco.',
  },

  // Manuale V1, rr. 2317-2323. **maxBonds: 6** (6 templates ↔ 6, FI-3)
  bondTemplates: [
    'Questa non è la mia prima avventura con _____, e ancora abbiamo qualcosa in sospeso.',
    "Ho cantato storie riguardo a _____ molto prima di averlo incontrato di persona. C'era un fondo di verità?",
    'Metto alla prova _____ con i miei scherzi, lo aiuterò a migliorarsi o mi farà solo divertire?',
    'Sto scrivendo le avventure di _____, sarà il soggetto adatto?',
    '_____ mi ha confidato un segreto.',
    '_____ non si fida di me, e per ottime ragioni!',
  ],
  maxBonds: 6,

  // Manuale V1, rr. 2325-2335
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        "Quando entri in un insediamento civilizzato, sarai sempre accolto da chi rispetta l'usanza dell'ospitalità per i menestrelli.",
      grantedMoveId: 'bardo.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando giungi per la prima volta in un luogo importante, puoi chiedere al GM un fatto della sua storia.',
      grantedMoveId: 'bardo.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description: 'Quando usi *Parole di Potere*, puoi tirare+**COS** al posto di +**CAR**.',
      grantedMoveId: 'bardo.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        "Quando usi *Piano d'Azione*, prendi +1 ai tiri per usare le opportunità generate.",
      grantedMoveId: 'bardo.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Scegli una classe (vedi mossa). Quando prendi una mossa multiclasse, puoi *sempre e comunque* scegliere tra le mosse di tale classe.',
      grantedMoveId: 'bardo.archetipo-mezzorco',
    },
  ],

  startingMoves: bardoStartingAutoMoves.map((m) => m.id),

  // Pattern nuovo: pickCount: 2 (manuale: "scegli DUE delle seguenti mosse")
  startingChoiceGroups: [
    {
      id: 'mosse-iniziali-aggiuntive',
      name: 'Mosse iniziali aggiuntive',
      description: 'Scegli DUE di queste quattro mosse oltre alle 3 iniziali automatiche.',
      moveOptions: bardoStartingChoiceMoves.map((m) => m.id),
      pickCount: 2,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: bardoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: bardoAdvanced6to10Moves.map((m) => m.id),

  // Il Bardo non è casteggiante: la sua "magia" emana dalla Parola, ma il
  // sistema *Lanciare Incantesimi* / *Mana* / Tradizioni Sacre non si applica.
  // spellcasting resta undefined.

  // Manuale V1, rr. 2281-2315
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
    ],
    choices: [
      {
        id: 'strumento-della-parola',
        prompt: 'Ciò con cui eserciti il potere della Parola',
        pickCount: 1,
        options: [
          {
            id: 'strumento-musicale',
            label: 'Strumento Musicale',
            items: [
              {
                id: 'strumento-musicale',
                name: 'Strumento Musicale',
                weight: 0,
                tags: [],
                userDescriptionPrompt: 'Descrivilo a tua scelta.',
              },
            ],
          },
          {
            id: 'discorsi-grande-oratore',
            label: 'Discorsi di un grande oratore',
            items: [
              {
                id: 'discorsi-grande-oratore',
                name: 'Discorsi di un grande oratore',
                weight: 0,
                tags: [],
              },
            ],
          },
          {
            id: 'battute-taglienti',
            label: 'Battute Taglienti di un famoso commediografo',
            items: [
              {
                id: 'battute-taglienti',
                name: 'Battute Taglienti di un famoso commediografo',
                weight: 0,
                tags: [],
              },
            ],
          },
          {
            id: 'quaderno-di-appunti',
            label: "Quaderno di appunti e osservazioni sull'animo umano",
            items: [
              {
                id: 'quaderno-di-appunti',
                name: "Quaderno di appunti e osservazioni sull'animo umano",
                weight: 0,
                tags: [],
              },
            ],
          },
          {
            id: 'sacchetto-simboli-runici',
            label: 'Sacchetto di antichi simboli runici',
            items: [
              {
                id: 'sacchetto-simboli-runici',
                name: 'Sacchetto di antichi simboli runici',
                weight: 0,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'abiti-iniziali',
        prompt: "Scegli un'opzione per i tuoi abiti",
        pickCount: 1,
        options: [
          {
            id: 'armatura-di-cuoio',
            label: 'Armatura di cuoio',
            items: [
              {
                id: 'armatura-di-cuoio',
                name: 'Armatura di cuoio',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
          {
            id: 'abiti-vistosi-piu-monete',
            label: 'Abiti vistosi e 6 monete',
            items: [
              {
                id: 'abiti-vistosi',
                name: 'Abiti vistosi',
                weight: 0,
                tags: ['indossato', 'vistoso'],
              },
              {
                id: 'monete',
                name: 'Monete',
                weight: 0,
                quantity: 6,
                tags: [],
                description: 'Soldi del PG.',
              },
            ],
          },
        ],
      },
      {
        id: 'arma-iniziale',
        prompt: 'Scegli la tua arma',
        pickCount: 1,
        options: [
          {
            id: 'stocco',
            label: 'Stocco',
            items: [
              {
                id: 'stocco',
                name: 'Stocco',
                weight: 2,
                tags: ['media', 'precisa'],
                armorPiercing: 1,
              },
            ],
          },
          {
            id: 'arco-faretra-spada-corta',
            label: 'Arco logoro + Faretra di frecce + Spada corta',
            items: [
              {
                id: 'arco-logoro',
                name: 'Arco logoro',
                weight: 2,
                tags: ['vicino', 'a due mani'],
              },
              {
                id: 'faretra-di-frecce',
                name: 'Faretra di frecce',
                weight: 1,
                uses: 3,
                tags: [],
                description: '3 munizioni.',
              },
              {
                id: 'spada-corta',
                name: 'Spada corta',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
            ],
          },
          {
            id: 'bende',
            label: 'Bende',
            items: [
              {
                id: 'bende',
                name: 'Bende',
                weight: 0,
                uses: 3,
                tags: ['lento'],
              },
            ],
          },
          {
            id: 'erba-pipa-halfling',
            label: 'Erba Pipa degli Halfling',
            items: [
              {
                id: 'erba-pipa-halfling',
                name: 'Erba Pipa degli Halfling',
                weight: 0,
                uses: 6,
                tags: [],
              },
            ],
          },
          {
            id: 'monete-x3',
            label: '3 monete',
            items: [
              {
                id: 'monete',
                name: 'Monete',
                weight: 0,
                quantity: 3,
                tags: [],
                description: 'Soldi del PG.',
              },
            ],
          },
        ],
      },
    ],
  },
};
